import React from 'react';
import {
    FileText,
    User,
    UserPlus
} from 'lucide-react';
import {IconLibrary} from './Icons';
const icons = IconLibrary.getAll();
  
const AppoinmentDetails = ({ appointmentData }) => {
    
    // Safety check for data
    if (!appointmentData) {
        return <div className="p-4 text-center text-gray-500">Loading appointment details...</div>;
    }

    // Extract necessary data manually given the nested structure
    // const { data } = appointmentData;
    const { 
        reasonforvisit, 
        reportDetails, 
        consultantInfo 
    } = appointmentData;

    const patientDetails = consultantInfo?.patientDetails || {};
    const basicInfo = patientDetails?.basicInfo || {};
    const medicalHistory = patientDetails?.medicalHistory || {};

    // Mapped Fields
    const patientComplaint = reasonforvisit;
    // Assuming we take reports from the first entry in reportDetails array for now
    const reports = reportDetails?.[0]?.reports || [];
    
    const allergies = basicInfo?.patientAllergies || [];
    const medications = medicalHistory?.patientMedication || [];
    const medicalConditions = basicInfo?.Medicalcondition || [];
    const patientSymptoms = basicInfo?.patientSymptoms || [];
    const surgeries = basicInfo?.patientSurgery || [];

    // Helper component for Section Headers
    const SectionHeader = ({ icon: Icon, title, colorClass = "bg-green-100 text-green-600", iconColor = "text-green-600",isize=22 }) => (
        <div className="section-header">
            <div className={`icon-box ${colorClass}`}>
                <Icon size={isize} className={iconColor} />
            </div>
            <h3>{title}</h3>
            <div className="separator"></div>
        </div>
    );

    // Helper for empty states
    const EmptyState = ({ message }) => (
        <div className="text-gray-400 text-xs italic py-1">{message}</div>
    );

    const getFileIcon = (fileType) => {
        // Simple mapping for icon text/color (could be enhanced)
        const type = String(fileType).toLowerCase();
        if (type.includes('pdf')) return <span className="text-red-500">PDF</span>;
        if (type.includes('jpg') || type.includes('jpeg') || type.includes('png')) return <span className="text-blue-500">IMG</span>;
        return <span className="text-gray-500">FILE</span>;
    };

    return (
        <div className="appointment-details">
            
            {/* Patient Complaint */}
            <div className='section-container'>
                <SectionHeader icon={icons.PatientComplaintIcon} title="Patient Complaint" colorClass="bg-green-100" iconColor="text-green-600" />
                <div className="content-box border-box p-2">
                   {patientComplaint|| <EmptyState message="No complaint recorded." />}
                </div>
            </div>

            {/* Reports */}
            <div className='section-container'>
                <SectionHeader icon={icons.ReportsIcon} title="Reports" colorClass="bg-purple-100" iconColor="text-purple-600" />
                <div className="grid-2">
                    {reports.length > 0 && reports.some(r => r.filename) ? (
                        reports.filter(r => r.filename).map((report, index) => (
                            <div key={index} className="report-card">
                                <div className="file-icon bg-gray-50 font-bold text-xs ring-1 ring-gray-100">
                                   {getFileIcon(report.filetypes)}
                                </div>
                                <div className="overflow-hidden flex-1">
                                     <a href={report.filecontent || "#"} target="_blank" rel="noopener noreferrer">
                                        {/* {report.filename} */}report{' '+(index+1)}
                                    </a>
                                </div>
                                <span className="ml-auto text-blue-400 text-xs">↗</span>
                            </div>
                        ))
                    ) : (
                        <EmptyState message="No reports attached." />
                    )}
                </div>
            </div>

            {/* Allergies */}
            <div className='section-container'>
                 <SectionHeader icon={icons.AllergiesIcon} title="Allergies" colorClass="bg-orange-100" iconColor="text-orange-500" />
                 {allergies.length > 0 ? (
                    <div className="border-box divide-y">
                        {allergies.map((allergy, index) => (
                            <div key={index} className="list-item">
                                <div className="item-name">{allergy.allergen}</div>
                                <div className="item-sub">
                                    {allergy.reaction ? `Reaction: ${allergy.reaction}` : ''} 
                                    {allergy.status ? ` | Status: ${allergy.status}` : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                 ) : (
                    <EmptyState message="No allergies recorded." />
                 )}
            </div>

            {/* Current Medications */}
            <div className='section-container'>
                <SectionHeader icon={icons.MedicationIcon} title="Current Medications" colorClass="bg-pink-100" iconColor="text-pink-500" />
                {medications.length > 0 ? (
                    <div className="border-box divide-y">
                        {medications.map((med, index) => (
                            <div key={index} className="list-item">
                                <div className="item-name">{med.medication} {med.quantity ? `(${med.quantity} ${med.measurement || ''})` : ''}</div>
                                <div className="item-sub">
                                     {med.frequency || ''} 
                                     {med.status ? ` | ${med.status}` : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <EmptyState message="No current medications." />
                )}
            </div>

             {/*Patient Symptoms */}
             <div className='section-container'>{console.log("sypmtoms",patientSymptoms)}
                <SectionHeader icon={icons.SymptomIcon} title="Symptoms" colorClass="bg-blue-100" iconColor="text-blue-500" isize={24}/>
                 {patientSymptoms.length > 0 ? (
                     <div className="border-box divide-y">
                        {patientSymptoms.map((symptom, index) => (
                            <div key={index} className="list-item">
                                <div className="item-name">{symptom.symptoms}</div>
                                <div className="item-sub">
                                    {symptom.since ? `Since: ${symptom.since} ${symptom.numberofdays || ''}` : ''}
                                    {symptom.status ? ` | ${symptom.status}` : ''}
                                </div>
                            </div>
                        ))}
                     </div>
                 ) : (
                     <EmptyState message="No medical conditions recorded." />
                 )}
            </div>

             {/* Medical Conditions */}
             <div className='section-container'>
                <SectionHeader icon={icons.MedicalConditionIcon} title="Medical Conditions" colorClass="bg-blue-100" iconColor="text-blue-500" isize={24}/>
                 {medicalConditions.length > 0 ? (
                     <div className="border-box divide-y">
                        {medicalConditions.map((condition, index) => (
                            <div key={index} className="list-item">
                                <div className="item-name">{condition.medicalcondition}</div>
                                <div className="item-sub">
                                    {condition.sincePeriod ? `Since: ${condition.sincePeriod} ${condition.numberofdays || ''}` : ''}
                                    {condition.status ? ` | ${condition.status}` : ''}
                                </div>
                            </div>
                        ))}
                     </div>
                 ) : (
                     <EmptyState message="No medical conditions recorded." />
                 )}
            </div>

             {/* Surgeries */}
             <div className='section-container'>
                <SectionHeader icon={icons.SurgeryIcon} title="Surgeries" colorClass="bg-[#F1CB9C]" iconColor="text-yellow-600" isize="18" />
                {surgeries.length > 0 ? (
                    <div className="border-box divide-y">
                        {surgeries.map((surgery, index) => (
                            <div key={index} className="list-item">
                                <div className="item-name">{surgery.surgery}</div>
                                 <div className="item-sub">
                                    {surgery.date ? `Date: ${surgery.date}` : ''}
                                    {/* Note: surgery object in example doesn't have status, but taking note instead */}
                                    {surgery.note ? <div className="mt-1 text-gray-500 ">{surgery.note}</div> : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                     <EmptyState message="No surgeries recorded." />
                )}
            </div>

        </div>
    );
};

export default AppoinmentDetails;